import React, { useEffect, useState } from "react";
import { Download, X, Share } from "lucide-react";

import { Button } from "../ui/button";
import { lerPreferencia, gravarPreferencia } from "../../services/preferencias";

// Convite para instalar o sistema como aplicativo.
//
// Instalado, ele abre sem a barra do navegador, ganha ícone na tela de início
// e passa a receber a notificação de mensagem nova como qualquer app. O
// navegador só oferece isso sozinho num cantinho do menu, que ninguém procura.
//
// Aparece uma vez; recusado, não volta a incomodar.
const CHAVE = "convite-instalar-app";

// O iPhone não dispara `beforeinstallprompt` e não tem botão de instalar:
// no Safari o caminho é Compartilhar → "Adicionar à Tela de Início", e é
// preciso dizer isso em palavras, senão o usuário de iPhone nunca instala.
const ehIphone = () =>
	/iphone|ipad|ipod/i.test(navigator.userAgent) &&
	!/crios|fxios/i.test(navigator.userAgent);

const jaInstalado = () =>
	window.matchMedia("(display-mode: standalone)").matches ||
	window.navigator.standalone === true;

const InstalarApp = () => {
	const [evento, setEvento] = useState(null);
	const [visivel, setVisivel] = useState(false);

	useEffect(() => {
		if (jaInstalado() || lerPreferencia(CHAVE) === "dispensado") return;

		// Android/Chrome: o navegador avisa quando o app é instalável.
		const aoPoderInstalar = (e) => {
			// Sem isto o Chrome mostra o próprio convite dele, e ficariam dois.
			e.preventDefault();
			setEvento(e);
			setVisivel(true);
		};

		window.addEventListener("beforeinstallprompt", aoPoderInstalar);

		// iPhone: não há evento, então o convite sai depois de um tempo de uso
		// — no primeiro segundo a pessoa ainda está entendendo a tela.
		let relogio;
		if (ehIphone()) {
			relogio = setTimeout(() => setVisivel(true), 20000);
		}

		return () => {
			window.removeEventListener("beforeinstallprompt", aoPoderInstalar);
			clearTimeout(relogio);
		};
	}, []);

	const dispensar = () => {
		gravarPreferencia(CHAVE, "dispensado");
		setVisivel(false);
	};

	const instalar = async () => {
		if (!evento) return;

		evento.prompt();
		const { outcome } = await evento.userChoice;

		// Instalado ou recusado, o evento só serve uma vez.
		setEvento(null);
		setVisivel(false);

		if (outcome === "accepted") gravarPreferencia(CHAVE, "instalado");
	};

	if (!visivel) return null;

	return (
		<div
			role="dialog"
			aria-label="Instalar aplicativo"
			// Acima da barra de navegação de baixo, respeitando a safe area.
			className="fixed inset-x-3 bottom-[calc(4.5rem+env(safe-area-inset-bottom))] z-40 flex items-start gap-3 rounded-xl border bg-card p-3 shadow-lg md:hidden"
		>
			<div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
				<Download className="h-4 w-4" />
			</div>

			<div className="min-w-0 flex-1">
				<p className="text-sm font-medium">Instalar na tela de início</p>
				{ehIphone() ? (
					<p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
						Toque em{" "}
						<Share className="inline h-3 w-3 align-[-1px]" /> na barra do
						Safari e escolha “Adicionar à Tela de Início”.
					</p>
				) : (
					<>
						<p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
							Abre sem a barra do navegador e fica com a tela inteira para a
							conversa.
						</p>
						<Button size="sm" className="mt-2 h-8" onClick={instalar}>
							Instalar
						</Button>
					</>
				)}
			</div>

			<Button
				variant="ghost"
				size="icon"
				className="h-8 w-8 shrink-0"
				aria-label="Agora não"
				onClick={dispensar}
			>
				<X className="h-4 w-4" />
			</Button>
		</div>
	);
};

export default InstalarApp;
